function hexToRgb(hex: string): [number, number, number] {
  const v = hex.startsWith("#") ? hex.slice(1) : hex;
  return [
    parseInt(v.slice(0, 2), 16),
    parseInt(v.slice(2, 4), 16),
    parseInt(v.slice(4, 6), 16),
  ];
}

function clamp(v: number): number {
  return v < 0 ? 0 : v > 255 ? 255 : v;
}

function clamp01(v: number): number {
  return v < 0 ? 0 : v > 1 ? 1 : v;
}

/** sRGB luminance (0..255 in, 0..255 out). */
function luma255(r: number, g: number, b: number): number {
  return 0.299 * r + 0.587 * g + 0.114 * b;
}

function rgbToHsl(r: number, g: number, b: number): [number, number, number] {
  r /= 255;
  g /= 255;
  b /= 255;
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const l = (max + min) / 2;
  let h = 0;
  let s = 0;
  const d = max - min;
  if (d !== 0) {
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    if (max === r) h = ((g - b) / d + (g < b ? 6 : 0)) / 6;
    else if (max === g) h = ((b - r) / d + 2) / 6;
    else h = ((r - g) / d + 4) / 6;
  }
  return [h, s, l];
}

function hue2rgb(p: number, q: number, t: number): number {
  if (t < 0) t += 1;
  if (t > 1) t -= 1;
  if (t < 1 / 6) return p + (q - p) * 6 * t;
  if (t < 1 / 2) return q;
  if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6;
  return p;
}

function hslToRgb(h: number, s: number, l: number): [number, number, number] {
  if (s === 0) {
    const v = l * 255;
    return [v, v, v];
  }
  const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
  const p = 2 * l - q;
  return [
    hue2rgb(p, q, h + 1 / 3) * 255,
    hue2rgb(p, q, h) * 255,
    hue2rgb(p, q, h - 1 / 3) * 255,
  ];
}

interface Rect {
  x: number;
  y: number;
  width: number;
  height: number;
}

function intersectsCanvas(canvas: HTMLCanvasElement, rect: Rect): Rect | null {
  const x = Math.max(0, Math.floor(rect.x));
  const y = Math.max(0, Math.floor(rect.y));
  const right = Math.min(canvas.width, Math.ceil(rect.x + rect.width));
  const bottom = Math.min(canvas.height, Math.ceil(rect.y + rect.height));
  const w = right - x;
  const h = bottom - y;
  if (w <= 0 || h <= 0) return null;
  return { x, y, width: w, height: h };
}

export function applyPhotoFilter(
  ctx: CanvasRenderingContext2D,
  region: Rect,
  hexColor: string,
  density: number,
  preserveLuminosity: boolean,
) {
  const rect = intersectsCanvas(ctx.canvas, region);
  if (!rect) return;
  const imageData = ctx.getImageData(rect.x, rect.y, rect.width, rect.height);
  const data = imageData.data;
  const [fR, fG, fB] = hexToRgb(hexColor);
  const d = Math.min(1, Math.max(0, density));
  for (let i = 0; i < data.length; i += 4) {
    const a = data[i + 3];
    if (a === 0) continue;
    const r = data[i];
    const g = data[i + 1];
    const b = data[i + 2];
    // multiply blend with filter color, mixed by density
    let nr = r * (1 - d) + (r * fR) / 255 * d;
    let ng = g * (1 - d) + (g * fG) / 255 * d;
    let nb = b * (1 - d) + (b * fB) / 255 * d;
    if (preserveLuminosity) {
      const lumIn = 0.299 * r + 0.587 * g + 0.114 * b;
      const lumOut = 0.299 * nr + 0.587 * ng + 0.114 * nb;
      if (lumOut > 0.001) {
        const k = lumIn / lumOut;
        nr *= k;
        ng *= k;
        nb *= k;
      }
    }
    data[i] = clamp(nr);
    data[i + 1] = clamp(ng);
    data[i + 2] = clamp(nb);
  }
  ctx.putImageData(imageData, rect.x, rect.y);
}

export function applyBlackAndWhite(
  ctx: CanvasRenderingContext2D,
  region: Rect,
  weights: { reds: number; yellows: number; greens: number; cyans: number; blues: number; magentas: number },
  tintHex?: string,
) {
  const rect = intersectsCanvas(ctx.canvas, region);
  if (!rect) return;
  const imageData = ctx.getImageData(rect.x, rect.y, rect.width, rect.height);
  const data = imageData.data;
  const w = {
    r: weights.reds / 100,
    y: weights.yellows / 100,
    g: weights.greens / 100,
    c: weights.cyans / 100,
    b: weights.blues / 100,
    m: weights.magentas / 100,
  };
  const tint = tintHex ? hexToRgb(tintHex) : null;
  for (let i = 0; i < data.length; i += 4) {
    const a = data[i + 3];
    if (a === 0) continue;
    const r = data[i] / 255;
    const g = data[i + 1] / 255;
    const b = data[i + 2] / 255;
    // Approximate PS B&W: derive RGB channel contributions by color region
    const max = Math.max(r, g, b);
    const min = Math.min(r, g, b);
    const chroma = max - min;
    let gray: number;
    if (chroma === 0) {
      gray = max;
    } else {
      // identify dominant hue region
      let h: number;
      if (max === r) h = ((g - b) / chroma + 6) % 6;
      else if (max === g) h = (b - r) / chroma + 2;
      else h = (r - g) / chroma + 4;
      // 0:r, 1:y, 2:g, 3:c, 4:b, 5:m
      const seg = h;
      const seg1 = Math.floor(seg);
      const t = seg - seg1;
      const order: Array<keyof typeof w> = ["r", "y", "g", "c", "b", "m"];
      const k1 = w[order[seg1 % 6]];
      const k2 = w[order[(seg1 + 1) % 6]];
      const channelWeight = k1 * (1 - t) + k2 * t;
      // luminance baseline + chroma scaled by hue weight
      const lum = min + chroma * channelWeight;
      gray = lum;
    }
    gray = Math.max(0, Math.min(1, gray));
    const g8 = gray * 255;
    if (tint) {
      // Tint: multiply gray with tint color
      data[i] = clamp((g8 * tint[0]) / 255);
      data[i + 1] = clamp((g8 * tint[1]) / 255);
      data[i + 2] = clamp((g8 * tint[2]) / 255);
    } else {
      data[i] = g8;
      data[i + 1] = g8;
      data[i + 2] = g8;
    }
  }
  ctx.putImageData(imageData, rect.x, rect.y);
}

/** Iterate the non-transparent pixels of a region, applying `fn(r,g,b) -> [r,g,b]`. */
function eachPixel(
  ctx: CanvasRenderingContext2D,
  region: Rect,
  fn: (r: number, g: number, b: number) => [number, number, number],
): void {
  const rect = intersectsCanvas(ctx.canvas, region);
  if (!rect) return;
  const imageData = ctx.getImageData(rect.x, rect.y, rect.width, rect.height);
  const data = imageData.data;
  for (let i = 0; i < data.length; i += 4) {
    if (data[i + 3] === 0) continue;
    const [nr, ng, nb] = fn(data[i], data[i + 1], data[i + 2]);
    data[i] = clamp(nr);
    data[i + 1] = clamp(ng);
    data[i + 2] = clamp(nb);
  }
  ctx.putImageData(imageData, rect.x, rect.y);
}

export function applyBrightnessContrast(
  ctx: CanvasRenderingContext2D,
  region: Rect,
  brightness: number,
  contrast: number,
) {
  // contrast (-50..100) -> classic factor pivoting at 127; brightness (-150..150) -> offset
  const c = contrast * 2.55;
  const factor = (259 * (c + 255)) / (255 * (259 - c));
  const offset = brightness * (127 / 150);
  eachPixel(ctx, region, (r, g, b) => [
    factor * (r - 127) + 127 + offset,
    factor * (g - 127) + 127 + offset,
    factor * (b - 127) + 127 + offset,
  ]);
}

export function applyHueSaturation(
  ctx: CanvasRenderingContext2D,
  region: Rect,
  hue: number,
  saturation: number,
  lightness: number,
) {
  const hShift = hue / 360;
  const sMul = 1 + saturation / 100;
  const lAmt = lightness / 100;
  eachPixel(ctx, region, (r, g, b) => {
    let [h, s, l] = rgbToHsl(r, g, b);
    h = (h + hShift) % 1;
    if (h < 0) h += 1;
    s = clamp01(s * sMul);
    if (lAmt > 0) l = l + (1 - l) * lAmt;
    else if (lAmt < 0) l = l + l * lAmt;
    return hslToRgb(h, s, l);
  });
}

export function applyVibrance(
  ctx: CanvasRenderingContext2D,
  region: Rect,
  vibrance: number,
  saturation: number,
) {
  const vib = vibrance / 100;
  const satFlat = saturation / 100;
  eachPixel(ctx, region, (r, g, b) => {
    const max = Math.max(r, g, b);
    const min = Math.min(r, g, b);
    const sat = max === 0 ? 0 : (max - min) / max; // HSV saturation 0..1
    // vibrance affects low-saturation pixels more; saturation is a flat boost
    const mul = Math.max(0, 1 + vib * (1 - sat) + satFlat);
    const lum = luma255(r, g, b);
    return [lum + (r - lum) * mul, lum + (g - lum) * mul, lum + (b - lum) * mul];
  });
}

interface Triplet {
  cyanRed: number;
  magentaGreen: number;
  yellowBlue: number;
}

export function applyColorBalance(
  ctx: CanvasRenderingContext2D,
  region: Rect,
  shadows: Triplet,
  midtones: Triplet,
  highlights: Triplet,
  preserveLuminosity: boolean,
) {
  const SCALE = 0.85; // amount 100 -> ~+85 on a 0..255 channel
  eachPixel(ctx, region, (r, g, b) => {
    const l = luma255(r, g, b) / 255;
    const shadowW = Math.max(0, 1 - 2 * l);
    const highW = Math.max(0, 2 * l - 1);
    const midW = 1 - shadowW - highW;
    const rOff =
      ((shadows.cyanRed * shadowW + midtones.cyanRed * midW + highlights.cyanRed * highW) / 100) * SCALE;
    const gOff =
      ((shadows.magentaGreen * shadowW + midtones.magentaGreen * midW + highlights.magentaGreen * highW) /
        100) *
      SCALE;
    const bOff =
      ((shadows.yellowBlue * shadowW + midtones.yellowBlue * midW + highlights.yellowBlue * highW) / 100) *
      SCALE;
    let nr = r + rOff;
    let ng = g + gOff;
    let nb = b + bOff;
    if (preserveLuminosity) {
      const lumIn = luma255(r, g, b);
      const lumOut = luma255(nr, ng, nb);
      if (lumOut > 0.001) {
        const k = lumIn / lumOut;
        nr *= k;
        ng *= k;
        nb *= k;
      }
    }
    return [nr, ng, nb];
  });
}

interface CMYK {
  c: number;
  m: number;
  y: number;
  k: number;
}

export function applySelectiveColor(
  ctx: CanvasRenderingContext2D,
  region: Rect,
  mode: "relative" | "absolute",
  colors: {
    reds: CMYK;
    yellows: CMYK;
    greens: CMYK;
    cyans: CMYK;
    blues: CMYK;
    magentas: CMYK;
    whites: CMYK;
    neutrals: CMYK;
    blacks: CMYK;
  },
) {
  const hueOrder: Array<keyof typeof colors> = [
    "reds",
    "yellows",
    "greens",
    "cyans",
    "blues",
    "magentas",
  ];
  eachPixel(ctx, region, (r, g, b) => {
    const rn = r / 255;
    const gn = g / 255;
    const bn = b / 255;
    const max = Math.max(rn, gn, bn);
    const min = Math.min(rn, gn, bn);
    const chroma = max - min;

    // accumulate CMYK deltas (in -1..1) weighted by membership
    let dc = 0;
    let dm = 0;
    let dy = 0;
    let dk = 0;
    const add = (range: CMYK, w: number) => {
      if (w <= 0) return;
      dc += (range.c / 100) * w;
      dm += (range.m / 100) * w;
      dy += (range.y / 100) * w;
      dk += (range.k / 100) * w;
    };

    if (chroma > 0) {
      let h: number;
      if (max === rn) h = ((gn - bn) / chroma + 6) % 6;
      else if (max === gn) h = (bn - rn) / chroma + 2;
      else h = (rn - gn) / chroma + 4;
      const seg = Math.floor(h) % 6;
      const t = h - Math.floor(h);
      add(colors[hueOrder[seg]], chroma * (1 - t));
      add(colors[hueOrder[(seg + 1) % 6]], chroma * t);
    }
    const whiteW = clamp01((min - 0.5) / 0.5);
    const blackW = clamp01((0.5 - max) / 0.5);
    const neutralW = clamp01(1 - chroma * 3) * (1 - whiteW - blackW);
    add(colors.whites, whiteW);
    add(colors.blacks, blackW);
    add(colors.neutrals, neutralW);

    // CMY increase darkens the matching channel; K darkens all
    let nc = 1 - rn;
    let nm = 1 - gn;
    let ny = 1 - bn;
    if (mode === "absolute") {
      nc += dc + dk;
      nm += dm + dk;
      ny += dy + dk;
    } else {
      nc += (dc + dk) * (1 - nc);
      nm += (dm + dk) * (1 - nm);
      ny += (dy + dk) * (1 - ny);
    }
    return [(1 - clamp01(nc)) * 255, (1 - clamp01(nm)) * 255, (1 - clamp01(ny)) * 255];
  });
}

export function applyGradientMap(
  ctx: CanvasRenderingContext2D,
  region: Rect,
  stops: Array<{ location: number; color: string }>,
  reverse: boolean,
) {
  if (stops.length === 0) return;
  const sorted = [...stops].sort((a, b) => a.location - b.location);
  const rgbStops = sorted.map((s) => ({ loc: s.location, rgb: hexToRgb(s.color) }));
  // Build a 256-entry LUT indexed by luminance
  const lut = new Uint8ClampedArray(256 * 3);
  for (let i = 0; i < 256; i++) {
    let t = i / 255;
    if (reverse) t = 1 - t;
    let lo = rgbStops[0];
    let hi = rgbStops[rgbStops.length - 1];
    for (let s = 0; s < rgbStops.length - 1; s++) {
      if (t >= rgbStops[s].loc && t <= rgbStops[s + 1].loc) {
        lo = rgbStops[s];
        hi = rgbStops[s + 1];
        break;
      }
    }
    const span = hi.loc - lo.loc;
    const f = span <= 0 ? 0 : (t - lo.loc) / span;
    lut[i * 3] = lo.rgb[0] + (hi.rgb[0] - lo.rgb[0]) * f;
    lut[i * 3 + 1] = lo.rgb[1] + (hi.rgb[1] - lo.rgb[1]) * f;
    lut[i * 3 + 2] = lo.rgb[2] + (hi.rgb[2] - lo.rgb[2]) * f;
  }
  eachPixel(ctx, region, (r, g, b) => {
    const idx = Math.round(luma255(r, g, b));
    return [lut[idx * 3], lut[idx * 3 + 1], lut[idx * 3 + 2]];
  });
}
